import { useFirestore, useFirestoreCollectionData } from "reactfire"
import { collection, query, orderBy, limit } from "firebase/firestore"
import { UNIDADES } from "../config/unidades"

// Íconos por entidad
const ENTIDAD_ICONO = {
  apoderado:  "👤",
  cuota:      "💰",
  egreso:     "📤",
  ingreso:    "🎁",
  inventario: "📦",
  usuario:    "🔑",
}

// Colores por acción
const ACCION_COLOR = {
  "creó":    { bg: "#dcfce7", texto: "#166534", icono: "➕" },
  "editó":   { bg: "#dbeafe", texto: "#1e40af", icono: "✏️" },
  "eliminó": { bg: "#fee2e2", texto: "#991b1b", icono: "🗑️" },
  "archivó": { bg: "#fef9c3", texto: "#854d0e", icono: "🗂️" },
  "restauró":{ bg: "#ede9fe", texto: "#5b21b6", icono: "♻️" },
}

function tiempoRelativo(seconds) {
  const ahora = Math.floor(Date.now() / 1000)
  const diff  = ahora - seconds
  if (diff < 60)           return "Hace un momento"
  if (diff < 3600)         return `Hace ${Math.floor(diff / 60)} min`
  if (diff < 86400)        return `Hace ${Math.floor(diff / 3600)} h`
  if (diff < 86400 * 2)    return "Ayer"
  if (diff < 86400 * 7)    return `Hace ${Math.floor(diff / 86400)} días`
  return new Date(seconds * 1000).toLocaleDateString("es-CL")
}

function agruparPorDia(registros) {
  const grupos = {}
  registros.forEach(r => {
    const fecha = new Date((r.fecha?.seconds || 0) * 1000)
    const clave = fecha.toLocaleDateString("es-CL", { weekday: "long", day: "numeric", month: "long" })
    if (!grupos[clave]) grupos[clave] = []
    grupos[clave].push(r)
  })
  return grupos
}

export default function RegistroActividad() {
  const firestore = useFirestore()
  const { data: actividad, status } = useFirestoreCollectionData(
    query(
      collection(firestore, "actividad"),
      orderBy("fecha.seconds", "desc"),
      limit(100)
    ),
    { idField: "id" }
  )

  if (status === "loading") return <div className="loading">Cargando actividad...</div>

  const lista   = actividad || []
  const grupos  = agruparPorDia(lista)
  const dias    = Object.keys(grupos)

  // Estadísticas rápidas
  const hoy     = new Date().toLocaleDateString("es-CL", { weekday: "long", day: "numeric", month: "long" })
  const hoyList = grupos[hoy] || []

  return (
    <div>
      {/* ── Resumen rápido ── */}
      <div style={{ display: "flex", gap: 12, marginBottom: 28, flexWrap: "wrap" }}>
        {[
          { label: "Movimientos hoy",      valor: hoyList.length,  color: "var(--verde-suave)",  texto: "var(--verde)", icono: "📅" },
          { label: "Total últimos 7 días", valor: lista.length,    color: "#dbeafe",             texto: "#1e40af",      icono: "📊" },
          { label: "Usuarios activos",     valor: [...new Set(lista.map(r => r.usuario))].length, color: "#ede9fe", texto: "#5b21b6", icono: "👥" },
        ].map(s => (
          <div key={s.label} style={{ flex: 1, minWidth: 140, background: s.color, borderRadius: 12, padding: "14px 18px" }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: s.texto }}>{s.icono} {s.valor}</div>
            <div style={{ fontSize: 12, color: s.texto, opacity: 0.8, marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {lista.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🕐</div>
          <div className="empty-text">No hay actividad registrada aún.<br /><span style={{ fontSize: 13 }}>Los movimientos aparecerán aquí a medida que se realicen acciones.</span></div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
          {dias.map(dia => (
            <div key={dia}>
              {/* Cabecera de día */}
              <div style={{
                fontSize: 13, fontWeight: 700, color: "var(--gris)",
                textTransform: "capitalize", letterSpacing: "0.04em",
                borderBottom: "1px solid var(--gris-borde)",
                paddingBottom: 8, marginBottom: 12,
              }}>
                📅 {dia}
              </div>

              {/* Registros del día */}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {grupos[dia].map(r => {
                  const accionCfg  = ACCION_COLOR[r.accion]  || { bg: "var(--verde-suave)", texto: "var(--verde)", icono: "•" }
                  const cfgUnidad  = r.unidadId ? UNIDADES[r.unidadId] : null
                  const entidadIco = ENTIDAD_ICONO[r.entidad] || "📋"

                  return (
                    <div
                      key={r.id}
                      style={{
                        display: "flex", alignItems: "flex-start", gap: 14,
                        background: "#fff", borderRadius: 12, padding: "14px 16px",
                        border: "1px solid var(--gris-borde)",
                        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                      }}
                    >
                      {/* Ícono de acción */}
                      <div style={{
                        width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                        background: accionCfg.bg,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 18,
                      }}>
                        {accionCfg.icono}
                      </div>

                      {/* Contenido */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--texto)", lineHeight: 1.4 }}>
                          <span style={{ color: accionCfg.texto }}>{r.accion}</span>
                          {" "}{entidadIco} {r.entidad}:{" "}
                          <span style={{ fontStyle: "italic" }}>"{r.nombre}"</span>
                        </div>

                        <div style={{ display: "flex", gap: 10, marginTop: 6, flexWrap: "wrap", alignItems: "center" }}>
                          {/* Unidad */}
                          {cfgUnidad && (
                            <span className="badge" style={{ background: cfgUnidad.bg, color: cfgUnidad.texto, fontSize: 11 }}>
                              {cfgUnidad.icono} {cfgUnidad.nombre}
                            </span>
                          )}
                          {/* Usuario */}
                          <span style={{ fontSize: 12, color: "var(--gris)" }}>
                            👤 {r.usuario}
                          </span>
                          {/* Detalle extra */}
                          {r.detalle && (
                            <span style={{ fontSize: 12, color: "var(--gris)", fontStyle: "italic" }}>
                              · {r.detalle}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Tiempo relativo */}
                      <div style={{ fontSize: 12, color: "var(--gris)", whiteSpace: "nowrap", flexShrink: 0 }}>
                        {tiempoRelativo(r.fecha?.seconds || 0)}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
